import React, { useCallback, useEffect, useState } from 'react';
import './SignupForm.css';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { register } from '../../features/register/registerThunks';
import { fetchCurrentUser } from '../../features/auth/authThunks';
import PhoneInput from '../../components/PhoneInputComponent/PhoneInput';
import { toast } from 'react-toastify';
import Button from "@mui/material/Button";
import { useLocation, useNavigate } from "react-router";
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import loaderStyles from "../../components/NavigationLoader/NavigationLoader.module.css";
import SimpleCaptcha, { createCaptchaChallenge, type CaptchaChallenge } from "../../components/SimpleCaptcha/SimpleCaptcha";
import { hasLeadingZeroPhoneDigit } from "../../utils/phoneValidation";
import { getCaptchaChallengeApi } from "../../api/captchaApi";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { getCountryValueDisplay } from "../../features/country/countrySlice";

const EyeIcon = FiEye as unknown as React.ComponentType<{ "aria-hidden"?: boolean }>;
const EyeOffIcon = FiEyeOff as unknown as React.ComponentType<{ "aria-hidden"?: boolean }>;

export interface SignupFormData {
  email: string;
  password: string;
  confirme_password?: string;
  firstName: string;
  lastName: string;
  nbr_child?: number | '';
  phone: string;
  codeCountry: string;
  indicatif: string;
  status: string;
  country: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  confirme_password?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  status?: string;
  nbr_child?: string;
  captcha?: string;
}

const NAME_MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 15;
const EMAIL_MAX_LENGTH = 50;
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 15;
const PASSWORD_COMPLEXITY_REGEX = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/;

const normalizeUserStatus = (value: string) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'single' || normalized === 'celibataire') return 'single';
  if (normalized === 'married' || normalized === 'marie_sans_enfants') return 'married';
  if (normalized === 'married_with_children' || normalized === 'marie_avec_enfants') return 'married_with_children';
  return '';
};

const SignupForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const location = useLocation();
  const isEditMode = location.pathname.includes('/profile/');

  const { user } = useSelector((state: RootState) => state.auth);
  const { countries, country: selectedCountry } = useSelector((state: RootState) => state.country);

  useEffect(() => {
    if(user && location.pathname.includes("register")){
      navigate('/');
    }
  }, [user, navigate, location]);

  const [formData, setFormData] = useState<SignupFormData>({
    email: '',
    password: '',
    confirme_password: '',
    firstName: '',
    lastName: '',
    nbr_child: '',
    phone: '',
    codeCountry: '',
    indicatif: '',
    status: '',
    country: '',
  });

  useEffect(() => {
    if (!user) return;

    setFormData((prev) => ({
      ...prev,
      email: user.email || "",
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      phone: user.phone || "",
      codeCountry: user.codeCountry || "",
      indicatif: user.indicatif || "",
      status: normalizeUserStatus(user.status || ""),
      nbr_child: Number.isInteger(user.nbr_child) ? user.nbr_child : '',
      country: getCountryValueDisplay(user.country, "", (i18n.resolvedLanguage || "fr").split("-")[0]),
      password: "",
      confirme_password: "",
    }));
  }, [i18n.resolvedLanguage, user]);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [phoneMaskDigits, setPhoneMaskDigits] = useState<number | null>(null);
  const [captchaChallenge, setCaptchaChallenge] = useState<CaptchaChallenge>(createCaptchaChallenge);
  const [captchaInput, setCaptchaInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const refreshCaptchaIfNeeded = useCallback(async () => {
    if (isEditMode) return;
    setCaptchaInput("");
    try {
      const challenge = await getCaptchaChallengeApi();
      setCaptchaChallenge(challenge);
    } catch {
      setCaptchaChallenge(createCaptchaChallenge());
      setErrors((prev) => ({ ...prev, captcha: t('system.serverError') }));
    }
  }, [isEditMode, t]);

  useEffect(() => {
    if (isEditMode) return;
    void refreshCaptchaIfNeeded();
  }, [isEditMode, refreshCaptchaIfNeeded]);

  const checkError = (name: any) => {
    switch (name) {
      case 'email':
        setErrors(prev => ({ ...prev, email: '' }));
        break;
      case 'password':
        setErrors(prev => ({ ...prev, password: '' }));
        break;
      case 'confirme_password':
        setErrors(prev => ({ ...prev, confirme_password: '' }));
        break;
      case 'firstName':
        setErrors(prev => ({ ...prev, firstName: '' }));
        break;
      case 'lastName':
        setErrors(prev => ({ ...prev, lastName: '' }));
        break;
      case 'phone':
        setErrors(prev => ({ ...prev, phone: '' }));
        break;
      case 'status':
        setErrors(prev => ({ ...prev, status: '' }));
        break;
      case 'nbr_child':
        setErrors(prev => ({ ...prev, nbr_child: '' }));
        break;
      case 'captcha':
        setErrors(prev => ({ ...prev, captcha: '' }));
        break;
      default:
        console.log('Aucune erreur à réinitialiser pour ce champ.');
    }
  };

  const handleChange = (event: any) => {
    const target = event.target;

    const name = target.name;
    const value = target.value;
    const mask = target.mask || '';
    const indicatif = typeof target.indicatif === 'string' ? target.indicatif : undefined;
    const country = typeof target.country === 'string' ? target.country : undefined;
    const codeCountry = typeof target.code === 'string' ? target.code : undefined;

    checkError(name);

    let expectedDigits: number | null = null;
    if (mask) {
      expectedDigits = (mask.match(/\d/g) || []).length;
      if (expectedDigits !== phoneMaskDigits) setPhoneMaskDigits(expectedDigits);
    }

    setFormData((prev) => {
      if (name === 'status') {
        return {
          ...prev,
          [name]: value,
          nbr_child: value === 'married_with_children' ? prev.nbr_child : '',
        };
      }

      if (name === 'nbr_child') {
        return {
          ...prev,
          nbr_child: value === '' ? '' : Number(value),
        };
      }

      return {
        ...prev,
        [name]: value,
        ...(indicatif !== undefined ? { indicatif } : {}),
        ...(country !== undefined ? { country } : {}),
        ...(codeCountry !== undefined ? { codeCountry } : {}),
      };
    });

    setErrors((prev) => {
      if (name !== 'phone') {
        return { ...prev, [name]: '' };
      }

      if (!prev.phone) return prev;

      const enteredDigits = value.replace(/\D/g, '').length;
      const shouldClear = expectedDigits && enteredDigits === expectedDigits;
      return { ...prev, phone: shouldClear ? '' : prev.phone };
    });
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    const lastNameValue = formData.lastName.trim();
    const firstNameValue = formData.firstName.trim();
    const emailValue = formData.email.trim();
    const passwordValue = formData.password.trim();

    if (!lastNameValue) newErrors.lastName = t('signup.errors.lastNameRequired');
    else if (lastNameValue.length < NAME_MIN_LENGTH) newErrors.lastName = t('signup.errors.lastNameMin', { count: NAME_MIN_LENGTH });
    else if (lastNameValue.length > NAME_MAX_LENGTH) newErrors.lastName = t('signup.errors.lastNameMax', { count: NAME_MAX_LENGTH });

    if (!firstNameValue) newErrors.firstName = t('signup.errors.firstNameRequired');
    else if (firstNameValue.length < NAME_MIN_LENGTH) newErrors.firstName = t('signup.errors.firstNameMin', { count: NAME_MIN_LENGTH });
    else if (firstNameValue.length > NAME_MAX_LENGTH) newErrors.firstName = t('signup.errors.firstNameMax', { count: NAME_MAX_LENGTH });

    if (!emailValue) newErrors.email = t('signup.errors.emailRequired');
    else if (emailValue.length > EMAIL_MAX_LENGTH) newErrors.email = t('signup.errors.emailMax', { count: EMAIL_MAX_LENGTH });
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue))
      newErrors.email = t('signup.errors.emailInvalid');

    if (!isEditMode) {
      if (!passwordValue) newErrors.password = t('signup.errors.passwordRequired');
      else if (passwordValue.length < PASSWORD_MIN_LENGTH) newErrors.password = t('signup.errors.passwordMin', { count: PASSWORD_MIN_LENGTH });
      else if (passwordValue.length > PASSWORD_MAX_LENGTH) newErrors.password = t('signup.errors.passwordMax', { count: PASSWORD_MAX_LENGTH });
      else if (!PASSWORD_COMPLEXITY_REGEX.test(passwordValue)) newErrors.password = t('signup.errors.passwordComplexity');

      if (formData.confirme_password !== formData.password) {
        newErrors.confirme_password = t('signup.errors.passwordMismatch');
      }
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('signup.errors.phoneRequired');
    } else if (hasLeadingZeroPhoneDigit(formData.phone)) {
      newErrors.phone = t('signup.errors.phoneLeadingZero');
    } else if (phoneMaskDigits && phoneMaskDigits > 0) {
      const enteredDigits = formData.phone.replace(/\D/g, '').length;
      if (enteredDigits !== phoneMaskDigits) {
        newErrors.phone = t('signup.errors.phoneExact', { count: phoneMaskDigits });
      }
    }

    if (!formData.status.trim()) newErrors.status = t('signup.errors.statusRequired');
    if (formData.status === 'married_with_children') {
      if (formData.nbr_child === '' || formData.nbr_child === undefined || formData.nbr_child === null) {
        newErrors.nbr_child = t('signup.errors.nbrChildRequired');
      } else if (
        !Number.isInteger(Number(formData.nbr_child)) ||
        Number(formData.nbr_child) < 0 ||
        Number(formData.nbr_child) > 7
      ) {
        newErrors.nbr_child = t('signup.errors.nbrChildInvalid');
      }
    }
    if (!isEditMode) {
      if (!captchaInput.trim()) {
        newErrors.captcha = t('captcha.errorRequired');
      } else if (!captchaChallenge.proof) {
        newErrors.captcha = t('system.serverError');
      } else if (captchaInput.trim().toUpperCase() !== captchaChallenge.code.toUpperCase()) {
        newErrors.captcha = t('captcha.errorInvalid');
      }
    }

    return newErrors;
  };

  const handleSubmit = async (event: Event) => {
    event.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      await refreshCaptchaIfNeeded();
      return;
    }

    setIsSubmitting(true);
    try {
      const currentIndicatif = String(formData.indicatif || '').trim();
      const normalizedCodeCountry = String(formData.codeCountry || '').trim().toUpperCase();
      const countryByCode = normalizedCodeCountry
        ? countries.find((item) => String(item?.code || '').trim().toUpperCase() === normalizedCodeCountry)
        : undefined;
      const fallbackIndicatif = String(countryByCode?.indicatif || selectedCountry?.indicatif || '').trim();
      const resolvedIndicatif = currentIndicatif || fallbackIndicatif;
      const resolvedCodeCountry = normalizedCodeCountry || String(selectedCountry?.code || '').trim().toUpperCase();

      const action = await dispatch(
        register({
          id: user?.id || null,
          email: formData.email,
          ...(!isEditMode && formData.password.trim() ? { password: formData.password } : {}),
          lastName: formData.lastName,
          firstName: formData.firstName,
          status: formData.status,
          nbr_child: formData.status === 'married_with_children' ? Number(formData.nbr_child) : 0,
          phone: formData.phone,
          codeCountry: resolvedCodeCountry,
          indicatif: resolvedIndicatif,
          country: formData.country,
          ...(!isEditMode ? {
            captcha: {
              input: captchaInput.trim(),
              code: captchaChallenge.code,
              proof: String(captchaChallenge.proof || ""),
            }
          } : {}),
        })
      ).unwrap();
      const rawSuccessMessage = isEditMode
        ? (typeof action?.message === 'string' ? action.message : t('signup.successDefault'))
        : t('signup.confirmEmailNotice');
      const successMessage = rawSuccessMessage.trim()
        ? `${rawSuccessMessage.trim().replace(/\.*$/, "")}.`
        : t('signup.successDefault');

      if (isEditMode) {
        await dispatch(fetchCurrentUser());
      }
      toast.success(successMessage, { autoClose: 3000 });
      navigate('/');
      setCaptchaInput("");
      await refreshCaptchaIfNeeded();

    } catch (error: any) {
      const apiErrorMessage = typeof error === 'string'
        ? error
        : (error?.fr || error?.message || t('system.registerError'));
      toast.error(apiErrorMessage);
      if (error?.codeError === 409) setErrors((prev) => ({ ...prev, email: error?.fr }));
      await refreshCaptchaIfNeeded();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="signup-container">
      <form className="signup-form" onSubmit={handleSubmit as any}>
        <h2>{t('signup.title')}</h2>
        <p className="signup-subtitle">{t('signup.subtitle')}</p>

        <div className="signup-row">
          <div className="signup-field">
            <label htmlFor="lastName">{t('signup.lastName')}</label>
            <input
              type="text"
              name="lastName"
              id="lastName"
              value={formData.lastName}
              maxLength={NAME_MAX_LENGTH}
              onChange={handleChange}
            />
            {errors.lastName && <p className="error-text">{errors.lastName}</p>}
          </div>

          <div className="signup-field">
            <label htmlFor="firstName">{t('signup.firstName')}</label>
            <input
              type="text"
              name="firstName"
              id="firstName"
              value={formData.firstName}
              maxLength={NAME_MAX_LENGTH}
              onChange={handleChange}
            />
            {errors.firstName && <p className="error-text">{errors.firstName}</p>}
          </div>
        </div>

        <div className={`signup-row ${formData.status === 'married_with_children' ? 'signup-row--triple' : ''}`}>
          <div className="signup-field">
            <label htmlFor="email">{t('signup.email')}</label>
            <input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              maxLength={EMAIL_MAX_LENGTH}
              onChange={handleChange}
            />
            {errors.email && <p className="error-text">{errors.email}</p>}
          </div>

          <div className="signup-field">
            <label htmlFor="status">{t('signup.status')}</label>
            <select
              name="status"
              id="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="">{t('signup.statusPlaceholder')}</option>
              <option value="single">{t('signup.statusSingle')}</option>
              <option value="married">{t('signup.statusMarriedNoChildren')}</option>
              <option value="married_with_children">{t('signup.statusMarriedWithChildren')}</option>
            </select>
            {errors.status && <p className="error-text">{errors.status}</p>}
          </div>

          {formData.status === 'married_with_children' && (
            <div className="signup-field">
              <label htmlFor="nbr_child">{t('signup.nbrChild')}</label>
              <select
                name="nbr_child"
                id="nbr_child"
                value={formData.nbr_child}
                onChange={handleChange}
              >
                <option value="">{t('signup.nbrChildPlaceholder')}</option>
                {Array.from({ length: 8 }, (_, i) => i).map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
              {errors.nbr_child && <p className="error-text">{errors.nbr_child}</p>}
            </div>
          )}
        </div>

        {!isEditMode && (
          <div className="signup-row">
              <div className="signup-field">
                <label htmlFor="password">{t('signup.password')}</label>
                <div className="signup-password-field">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    id="password"
                    value={formData.password}
                    maxLength={PASSWORD_MAX_LENGTH}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="signup-password-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? t('header.hidePassword') : t('header.showPassword')}
                    title={showPassword ? t('header.hidePassword') : t('header.showPassword')}
                  >
                    {showPassword ? <EyeOffIcon aria-hidden={true} /> : <EyeIcon aria-hidden={true} />}
                  </button>
                </div>
                {errors.password && <p className="error-text">{errors.password}</p>}
              </div>

              <div className="signup-field">
                <label htmlFor="confirme_password">{t('signup.confirmPassword')}</label>
                <div className="signup-password-field">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirme_password"
                    id="confirme_password"
                    value={formData.confirme_password}
                    maxLength={PASSWORD_MAX_LENGTH}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="signup-password-toggle"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label={showConfirmPassword ? t('header.hidePassword') : t('header.showPassword')}
                    title={showConfirmPassword ? t('header.hidePassword') : t('header.showPassword')}
                  >
                    {showConfirmPassword ? <EyeOffIcon aria-hidden={true} /> : <EyeIcon aria-hidden={true} />}
                  </button>
                </div>
                {errors.confirme_password && (
                  <p className="error-text">{errors.confirme_password}</p>
                )}
            </div>
          </div>
        )}

        <label htmlFor="phone">{t('signup.phone')}</label>
        <div className="select-wrapper">
          <PhoneInput onChange={handleChange} value={formData.phone} name="phone" />
        </div>
        {errors.phone && <p className="error-text">{errors.phone}</p>}
        <div className="space"></div>

        {!isEditMode && (
          <>
            <SimpleCaptcha
              idPrefix="signup"
              challenge={captchaChallenge}
              value={captchaInput}
              onChange={(value) => {
                setCaptchaInput(value);
                checkError("captcha");
              }}
              onRefresh={() => {
                checkError("captcha");
                void refreshCaptchaIfNeeded();
              }}
              label={t('captcha.label')}
              placeholder={t('captcha.placeholder')}
              refreshLabel={t('captcha.refresh')}
              error={errors.captcha}
            />
            <div className="space"></div>
          </>
        )}

        <Box
          className="signup-actions"
          sx={{
            display: "flex",
            justifyContent: {
              xs: "stretch", // mobile
              sm: "center",  // desktop
            },
          }}
        >
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="contained"
            sx={{
              width: {
                xs: "100%",
                sm: "auto",
              },
              backgroundColor: isSubmitting ? "grey.400" : "primary.main",
              color: "white",
              "&:disabled": {
                backgroundColor: "grey.400",
                color: "white",
              },
            }}
          >
            {user ? t('signup.update') : t('signup.save')}
          </Button>
        </Box>

      </form>
      {isSubmitting && (
        <div className={`${loaderStyles.overlay} ${loaderStyles.active}`} role="status" aria-live="polite">
          <div className={loaderStyles.spinner} aria-hidden="true" />
          <p className={loaderStyles.label}>{t('loader.pageLoading')}</p>
        </div>
      )}
    </div>
  );
};

export default SignupForm;
